import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import {
  useBeforeUnload,
  useBlocker,
  useLocation,
  type BlockerFunction,
} from "react-router";

import { Card, Page } from "../../app/layout/Page";
import { reportLifecycleDiagnostic } from "../../app/api/lifecycleDiagnostics";
import { queryKeys } from "../../app/query/queryClient";
import { catalogApi as defaultCatalogApi } from "../catalog/api";
import { curveImportApi } from "./api";
import { AuthenticationContext } from "../../app/auth/authState";
import { curveLibraryApi, type CurveLibraryApi } from "./libraryApi";
import { CurveAnalysisPanel } from "./CurveAnalysisPanel";
import { CalibrationEditor } from "./CalibrationEditor";
import { CurveDigitizer } from "./CurveDigitizer";
import { DiscardImportPanel } from "./DiscardImportPanel";
import { documentFromImport, EMPTY_CURVE_DOCUMENT } from "./editorState";
import { createCurveHistory, curveHistoryReducer } from "./history";
import {
  isProtectedDiscardError,
  readableCurveError as readableError,
} from "./errors";
import { ReviewPublicationPanel } from "./ReviewPublicationPanel";
import { SimpleCurveDataEditor } from "./SimpleCurveDataEditor";

import type { CurveImportApi } from "./api";
import type { CatalogApi } from "../catalog/api";
import type {
  CurveDraftDocument,
  CurveImportListItem,
  CurveImportRead,
  DiscardImportResult,
  ProcessingJobRead,
} from "./types";

import "./curveImport.css";
import { guided } from "../../app/guidance/fieldHelpRegistryCore";
import { curveImportFieldHelp } from "../../app/guidance/fieldHelpRegistry";

interface CurveImportWorkspaceProps {
  api?: CurveImportApi;
  catalog?: CatalogApi;
  library?: CurveLibraryApi;
}

const TERMINAL_JOB_STATES = new Set<ProcessingJobRead["state"]>([
  "completed",
  "failed",
  "cancelled",
  "timed_out",
]);

function stateLabel(
  state: CurveImportRead["state"],
  sourceFormat: CurveImportRead["source_format"],
): string {
  const labels: Record<CurveImportRead["state"], string> = {
    needs_calibration:
      sourceFormat === "pdf" ? "Ready to analyze" : "Ready for values",
    needs_review: "Ready to save",
    parsed: sourceFormat === "pdf" ? "Ready to analyze" : "Ready for values",
    rejected_insufficient: "Could not use source",
    uploaded: "Ready to read",
    verified: "Saved",
  };
  return labels[state];
}

export function CurveImportWorkspace({
  api = curveImportApi,
  catalog = defaultCatalogApi,
  library = curveLibraryApi,
}: CurveImportWorkspaceProps) {
  const authentication = useContext(AuthenticationContext);
  // Local workspaces use the application's same-origin session, not hosted accounts.
  // Private library requests establish that session and the server checks its scope.
  const localLibrary = authentication?.mode === "local";
  const libraryAuthenticated = localLibrary || (authentication?.mode === "hosted-authenticated" && authentication.session !== null);
  const location = useLocation();
  const queryClient = useQueryClient();
  const importKeys = queryKeys.resource("curve-imports");
  const [history, dispatch] = useReducer(
    curveHistoryReducer,
    EMPTY_CURVE_DOCUMENT,
    createCurveHistory,
  );
  const [activeImport, setActiveImport] = useState<CurveImportRead | null>(
    null,
  );
  const [activeTraceId, setActiveTraceId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const sourceOperationPending = useRef(false);
  const [enableOcr, setEnableOcr] = useState(false);
  const [selectedPageOnly, setSelectedPageOnly] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [lastJob, setLastJob] = useState<ProcessingJobRead | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [simpleDirty, setSimpleDirty] = useState(false);
  const [reviewDirty, setReviewDirty] = useState(false);
  const [graphSelectionDirty, setGraphSelectionDirty] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [discardBusy, setDiscardBusy] = useState(false);
  const [aiApplyBusy, setAiApplyBusy] = useState(false);
  const [advancedEditorBusy, setAdvancedEditorBusy] = useState(false);
  const [libraryBusy, setLibraryBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showSourceUpload, setShowSourceUpload] = useState(false);
  const [graphEditorRequest, setGraphEditorRequest] = useState(0);
  const [showLeavePrompt, setShowLeavePrompt] = useState(false);
  const [leaveError, setLeaveError] = useState("");
  const [dashboardDelete, setDashboardDelete] =
    useState<CurveImportListItem | null>(null);
  const [hiddenImportIds, setHiddenImportIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [showArchivedImports, setShowArchivedImports] = useState(false);
  const handledJob = useRef<string | null>(null);
  const simpleDirtyRef = useRef(simpleDirty);
  const advancedToolsRef = useRef<HTMLDetailsElement | null>(null);
  const advancedEditorHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const leaveDialogRef = useRef<HTMLDivElement | null>(null);
  const leaveStayButtonRef = useRef<HTMLButtonElement | null>(null);
  const leaveTriggerRef = useRef<HTMLButtonElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const openedLocationKey = useRef<string | null>(null);
  simpleDirtyRef.current = simpleDirty;
  const hasNonGraphUnsavedChanges = dirty || simpleDirty || reviewDirty;
  const hasAnyUnsavedChanges = hasNonGraphUnsavedChanges || graphSelectionDirty;
  const stateReplacingBusy =
    libraryBusy ||
    reviewBusy ||
    discardBusy ||
    advancedEditorBusy ||
    aiApplyBusy ||
    busyAction === "check" ||
    busyAction === "save" ||
    busyAction === "save-exit";
  const unsafeToLeave = hasAnyUnsavedChanges || stateReplacingBusy;
  const closeLeavePrompt = useCallback(() => {
    const trigger = leaveTriggerRef.current;
    setShowLeavePrompt(false);
    setLeaveError("");
    window.requestAnimationFrame(() => {
      if (trigger?.isConnected === true) {
        trigger.focus({ preventScroll: true });
      }
      leaveTriggerRef.current = null;
    });
  }, []);

  useBeforeUnload(
    useCallback(
      (event) => {
        if (unsafeToLeave) {
          event.preventDefault();
          event.returnValue = "";
        }
      },
      [unsafeToLeave],
    ),
  );
  const navigationBlocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        unsafeToLeave && currentLocation.pathname !== nextLocation.pathname,
      [unsafeToLeave],
    ),
  );

  useEffect(() => {
    if (navigationBlocker.state !== "blocked") return;
    if (
      window.confirm(
        "Leave this page and discard your unchecked pump curve changes?",
      )
    ) {
      navigationBlocker.proceed();
    } else {
      navigationBlocker.reset();
    }
  }, [navigationBlocker]);

  useEffect(() => {
    if (!showLeavePrompt) return;
    const frame = window.requestAnimationFrame(() => {
      leaveStayButtonRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [showLeavePrompt]);

  useEffect(() => {
    if (!showLeavePrompt) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && busyAction === null) {
        event.preventDefault();
        event.stopPropagation();
        closeLeavePrompt();
        return;
      }
      if (event.key !== "Tab") return;
      const dialog = leaveDialogRef.current;
      if (dialog === null) return;
      const buttons = Array.from(
        dialog.querySelectorAll<HTMLButtonElement>("button:not(:disabled)"),
      );
      const first = buttons.at(0);
      const last = buttons.at(-1);
      if (first === undefined || last === undefined) {
        event.preventDefault();
        return;
      }
      const active = window.document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (active === last || !dialog.contains(active))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    window.document.addEventListener("keydown", handleKeyDown);
    return () => window.document.removeEventListener("keydown", handleKeyDown);
  }, [busyAction, closeLeavePrompt, showLeavePrompt]);

  const capabilities = useQuery({
    queryFn: () => api.capabilities(),
    queryKey: ["flow-wizard", "curve-import-capabilities"],
  });
  const recentImports = useQuery({
    queryFn: () => api.listImports(),
    queryKey: importKeys.list({ limit: 100 }),
  });
  const activeJob = useQuery({
    enabled: jobId !== null,
    queryFn: () => {
      if (jobId === null) throw new Error("No processing job is selected.");
      return api.getJob(jobId);
    },
    queryKey: ["flow-wizard", "curve-import-job", jobId],
    refetchInterval: jobId === null ? false : 500,
  });
  const clearSourceSelection = useCallback(() => {
    setSelectedFile(null);
  }, []);

  useEffect(() => {
    if (showSourceUpload) uploadInputRef.current?.focus();
  }, [showSourceUpload, capabilities.data]);

  const activate = useCallback((imported: CurveImportRead) => {
    setActiveImport(imported);
    dispatch({ document: documentFromImport(imported), type: "reset" });
    setActiveTraceId(imported.traces[0]?.id ?? null);
    clearSourceSelection();
    setDirty(false);
    setSimpleDirty(false);
    setReviewDirty(false);
    setGraphSelectionDirty(false);
    setReviewBusy(false);
    setDiscardBusy(false);
    setAiApplyBusy(false);
    setAdvancedEditorBusy(false);
    setLibraryBusy(false);
    setError("");
    setShowSourceUpload(false);
    setGraphEditorRequest(0);
    setShowLeavePrompt(false);
    setLeaveError("");
    leaveTriggerRef.current = null;
    if (advancedToolsRef.current !== null)
      advancedToolsRef.current.open = false;
  }, [clearSourceSelection]);

  const resolveUploadedCurve = useCallback(async (imported: CurveImportRead) => {
    if (!libraryAuthenticated || imported.state === "verified" || imported.traces.length > 0) return null;
    setLibraryBusy(true);
    try {
      const result = await library.resolveImport(imported.id);
      return result.imported;
    } catch {
      throw new Error("Could not prepare this curve. Try again.");
    } finally {
      setLibraryBusy(false);
    }
  }, [library, libraryAuthenticated]);

  useEffect(() => {
    const state: unknown = location.state;
    if (state === null || typeof state !== "object" || !("importId" in state) ||
      typeof state.importId !== "string" || openedLocationKey.current === location.key) return;
    openedLocationKey.current = location.key;
    setBusyAction("open");
    void api.getImport(state.importId).then(activate)
      .catch((reason: unknown) => setError(readableError(reason)))
      .finally(() => setBusyAction(null));
  }, [activate, api, location.key, location.state]);

  useEffect(() => {
    const job = activeJob.data;
    if (
      job === undefined ||
      !TERMINAL_JOB_STATES.has(job.state) ||
      handledJob.current === job.id
    ) {
      return;
    }
    handledJob.current = job.id;
    setLastJob(job);
    if (job.state === "completed") {
      void api
        .getImport(job.import_id)
        .then(async (imported) => {
          activate(imported);
          const resolved = await resolveUploadedCurve(imported);
          if (resolved !== null) {
            activate(resolved);
            setNotice("Curve data is ready to review.");
            return queryClient.invalidateQueries({ queryKey: importKeys.all });
          }
          setNotice(
            imported.state === "rejected_insufficient"
              ? "Flow Wizard could not safely read this sheet. Review the reason below."
              : imported.state === "needs_review"
                ? "Curve data is ready to review."
                : imported.source_format === "pdf"
                  ? "The sheet is ready to analyze."
                  : "The curve is ready for values.",
          );
          return queryClient.invalidateQueries({ queryKey: importKeys.all });
        })
        .catch((reason: unknown) => setError(readableError(reason)))
        .finally(() => setJobId(null));
    } else {
      setError(
        readableError(
          new Error(job.error ?? `Processing ${job.state.replace("_", " ")}.`),
        ),
      );
      setJobId(null);
    }
  }, [activeJob.data, activate, api, importKeys.all, queryClient, resolveUploadedCurve]);

  const commitDocument = (document: CurveDraftDocument) => {
    if (simpleDirtyRef.current) {
      setError(
        "Check the simple pump values before using advanced curve editing.",
      );
      return;
    }
    dispatch({ document, type: "commit" });
    setDirty(true);
  };

  const chooseFile = (event: ChangeEvent<HTMLInputElement>) => {
    if (busyAction !== null) return;
    const file = event.target.files?.[0] ?? null;
    clearSourceSelection();
    reportLifecycleDiagnostic(
      file === null ? "file_selection_cancelled" : "file_selection_completed",
      "curve_import",
      file === null ? "cancelled" : "succeeded",
    );

    if (
      file !== null &&
      capabilities.data !== undefined &&
      file.size > capabilities.data.max_file_bytes
    ) {
      setSelectedFile(null);
      setError(
        `The selected file is ${file.size.toLocaleString()} bytes; the local limit is ${capabilities.data.max_file_bytes.toLocaleString()} bytes.`,
      );
      event.target.value = "";
      return;
    }
    setSelectedFile(file);
    setError("");
  };

  const startProcessingForImport = async (
    imported: CurveImportRead,
    useOcr: boolean,
    selectedPages: number[] | null,
  ) => {
    if (imported.state !== "uploaded") {
      setError(
        "This sheet has already been read. Add it again to run a new source scan.",
      );
      return;
    }
    setEnableOcr(useOcr);
    setBusyAction("parse");
    setError("");
    try {
      const job = await api.startParse(imported.id, useOcr, selectedPages);
      handledJob.current = null;
      setLastJob(job);
      setJobId(job.id);
      setNotice("Reading the vendor sheet…");
    } catch (reason: unknown) {
      setError(readableError(reason));
    } finally {
      setBusyAction(null);
    }
  };

  const upload = async (file: File) => {
    if (capabilities.data === undefined || sourceOperationPending.current || busyAction !== null) return;
    sourceOperationPending.current = true;
    reportLifecycleDiagnostic(
      "import_preview_started",
      "curve_import",
      "started",
    );
    setBusyAction("upload");
    setError("");
    try {
      const imported = await api.createImport(
        file,
        capabilities.data.max_file_bytes,
      );
      activate(imported);
      setSelectedFile(null);
      reportLifecycleDiagnostic(
        "import_preview_completed",
        "curve_import",
        "succeeded",
      );
      setNotice("Preparing curve…");
      const resolved = await resolveUploadedCurve(imported);
      if (resolved !== null) {
        activate(resolved);
        setNotice("Curve data is ready to review.");
      } else if (imported.state === "uploaded") {
        await startProcessingForImport(
          imported,
          capabilities.data.ocr_available === true,
          null,
        );
      } else {
        setNotice(
          imported.source_format === "pdf"
            ? "Vendor sheet added. Analyze it next."
            : "Vendor sheet added. Enter values next.",
        );
      }
      await queryClient.invalidateQueries({ queryKey: importKeys.all });
    } catch (reason: unknown) {
      reportLifecycleDiagnostic(
        "import_preview_failed",
        "curve_import",
        "failed",
      );
      setError(readableError(reason));
    } finally {
      sourceOperationPending.current = false;
      setBusyAction(null);
    }
  };

  const openImport = async (id: string) => {
    if (sourceOperationPending.current || busyAction !== null) return;
    sourceOperationPending.current = true;
    setBusyAction("open");
    setError("");
    try {
      const imported = await api.getImport(id);
      activate(imported);
      const resolved = await resolveUploadedCurve(imported);
      if (resolved !== null) {
        activate(resolved);
        setNotice("Curve data is ready to review.");
      }
    } catch (reason: unknown) {
      setError(readableError(reason));
    } finally {
      sourceOperationPending.current = false;
      setBusyAction(null);
    }
  };

  const deleteDashboardImport = async () => {
    if (dashboardDelete === null || busyAction !== null) return;
    const item = dashboardDelete;
    const reason = "Removed from the pump curve dashboard.";
    setBusyAction("delete-dashboard");
    setError("");
    try {
      try {
        await api.discardImport(item.id, {
          expected_original_filename: item.source_filename,
          expected_source_sha256: item.source_sha256,
          reason,
        });
        setNotice(`${item.source_filename} deleted.`);
      } catch (discardError: unknown) {
        if (!isProtectedDiscardError(discardError)) throw discardError;
        await api.archiveImport(item.id, reason);
        setNotice(`${item.source_filename} removed. Required history was kept.`);
      }
      setHiddenImportIds((current) => {
        const next = new Set(current);
        next.add(item.id);
        return next;
      });
      setDashboardDelete(null);
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
    } catch (reason: unknown) {
      setError(readableError(reason, "This pump curve could not be deleted."));
    } finally {
      setBusyAction(null);
    }
  };

  const startProcessing = async () => {
    if (activeImport === null || activeImport.state !== "uploaded") return;
    const selectedPages =
      selectedPageOnly && history.present.selected_page !== null
        ? [history.present.selected_page]
        : null;
    await startProcessingForImport(activeImport, enableOcr, selectedPages);
  };

  const cancelProcessing = async () => {
    if (jobId === null) return;
    setBusyAction("cancel");
    try {
      const job = await api.cancelJob(jobId);
      setLastJob(job);
      if (TERMINAL_JOB_STATES.has(job.state)) setJobId(null);
      setNotice("Cancellation requested.");
    } catch (reason: unknown) {
      setError(readableError(reason));
    } finally {
      setBusyAction(null);
    }
  };

  const confirmReplacingReviewEdits = () =>
    !reviewDirty ||
    window.confirm(
      "Checking curve values will discard your unchecked pump setup, reviewer, and attestation edits if the check succeeds. Continue?",
    );

  const saveDraft = async () => {
    if (activeImport === null || graphSelectionDirty || stateReplacingBusy)
      return;
    if (!confirmReplacingReviewEdits()) {
      setNotice("Curve check cancelled. Your pump setup edits were kept.");
      return;
    }
    setBusyAction("save");
    setError("");
    try {
      const imported = await api.updateDraft(activeImport.id, history.present);
      activate(imported);
      setNotice("Pump values checked. Review any messages below.");
      await queryClient.invalidateQueries({ queryKey: importKeys.all });
    } catch (reason: unknown) {
      setError(readableError(reason));
    } finally {
      setBusyAction(null);
    }
  };

  const checkSimpleDocument = async (document: CurveDraftDocument) => {
    if (
      activeImport === null ||
      graphSelectionDirty ||
      stateReplacingBusy ||
      (activeImport.state !== "needs_calibration" &&
        activeImport.state !== "needs_review")
    ) {
      return;
    }
    if (!confirmReplacingReviewEdits()) {
      throw new Error(
        "Curve check cancelled. Your pump setup edits were kept.",
      );
    }
    setBusyAction("check");
    setError("");
    try {
      const imported = await api.updateDraft(activeImport.id, document);
      activate(imported);
      setNotice(
        imported.state === "needs_review"
          ? "Curve checked. Choose the pump and save it for planning."
          : "Values saved. Fix the item shown below, then check the curve again.",
      );
      await queryClient.invalidateQueries({ queryKey: importKeys.all });
    } catch (reason: unknown) {
      const message = readableError(reason);
      setError(message);
      throw new Error(message);
    } finally {
      setBusyAction(null);
    }
  };

  const acceptImported = (imported: CurveImportRead) => {
    activate(imported);
    void queryClient.invalidateQueries({ queryKey: importKeys.all });
  };

  const openAdvancedTools = () => {
    const details = advancedToolsRef.current;
    if (details === null) return;
    details.open = true;
    const target = advancedEditorHeadingRef.current;
    if (target === null) {
      details.querySelector("summary")?.focus();
    } else {
      target.focus({ preventScroll: true });
    }
    const scrollTarget = target ?? details;
    if (typeof scrollTarget.scrollIntoView === "function") {
      scrollTarget.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  const acceptDiscarded = (result: DiscardImportResult) => {
    const filename = activeImport?.source.original_filename ?? "Import";
    setActiveImport(null);
    dispatch({ document: EMPTY_CURVE_DOCUMENT, type: "reset" });
    setActiveTraceId(null);
    setDirty(false);
    setSimpleDirty(false);
    setReviewDirty(false);
    setGraphSelectionDirty(false);
    setDiscardBusy(false);
    setJobId(null);
    setLastJob(null);
    setError("");
    setShowLeavePrompt(false);
    setLeaveError("");
    setNotice(
      result.deleted_attachment
        ? `${filename} and its unreferenced source attachment were permanently discarded.`
        : result.attachment_cleanup_pending
          ? `${filename} was permanently discarded. Its unreferenced source attachment is quarantined and pending physical cleanup.`
          : `${filename} was permanently discarded. A shared or protected source attachment was retained.`,
    );
    void queryClient.invalidateQueries({ queryKey: importKeys.all });
  };

  const returnToCurveLibrary = (discardedUncheckedChanges = false) => {
    setActiveImport(null);
    dispatch({ document: EMPTY_CURVE_DOCUMENT, type: "reset" });
    setActiveTraceId(null);
    clearSourceSelection();
    setDirty(false);
    setSimpleDirty(false);
    setReviewDirty(false);
    setGraphSelectionDirty(false);
    setReviewBusy(false);
    setDiscardBusy(false);
    setAiApplyBusy(false);
    setAdvancedEditorBusy(false);
    setJobId(null);
    setLastJob(null);
    setError("");
    setNotice(
      discardedUncheckedChanges ? "Unchecked changes discarded." : "",
    );
    setShowSourceUpload(false);
    setShowLeavePrompt(false);
    setLeaveError("");
    setGraphEditorRequest(0);
    leaveTriggerRef.current = null;
  };

  const requestReturnToCurveLibrary = (trigger: HTMLButtonElement) => {
    if (stateReplacingBusy || jobId !== null || busyAction !== null) return;
    if (hasAnyUnsavedChanges) {
      leaveTriggerRef.current = trigger;
      setLeaveError("");
      setShowLeavePrompt(true);
      return;
    }
    returnToCurveLibrary();
  };

  const saveAdvancedChangesAndExit = async () => {
    if (
      activeImport === null ||
      !dirty ||
      graphSelectionDirty ||
      simpleDirty ||
      reviewDirty ||
      stateReplacingBusy ||
      busyAction !== null
    ) {
      return;
    }
    setBusyAction("save-exit");
    setLeaveError("");
    try {
      await api.updateDraft(activeImport.id, history.present);
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
      returnToCurveLibrary();
      setNotice("Progress saved.");
    } catch (reason: unknown) {
      setLeaveError(readableError(reason, "Changes could not be saved."));
    } finally {
      setBusyAction(null);
    }
  };

  const displayedJob = activeJob.data ?? lastJob;
  const editable =
    activeImport !== null &&
    (activeImport.state === "needs_calibration" ||
      activeImport.state === "needs_review");
  const simpleEditable =
    activeImport !== null &&
    (activeImport.state === "needs_calibration" ||
      activeImport.state === "needs_review");
  const simpleEditorVisible =
    simpleEditable &&
    (activeImport?.source_format !== "pdf" ||
      history.present.traces.filter((trace) => trace.kind === "head").length <=
        1);
  const activeTrace =
    history.present.traces.find((trace) => trace.id === activeTraceId) ?? null;
  const hasExistingAiRun =
    (activeImport?.suggestions.ai_suggestion_runs.length ?? 0) > 0;
  const hasReviewableTraces = (activeImport?.traces.length ?? 0) > 0;
  const curveDataReady =
    hasReviewableTraces &&
    (activeImport?.state === "needs_review" ||
      activeImport?.state === "verified");
  const graphStepApplies =
    activeImport?.source_format === "pdf" &&
    activeImport.state !== "verified" &&
    activeImport.state !== "rejected_insufficient" &&
    (activeImport.selected_chart !== null ||
      (!hasExistingAiRun && !hasReviewableTraces));
  const graphSelectionRequired =
    graphStepApplies && activeImport?.selected_chart === null;
  const graphSelected = !graphSelectionRequired;
  const onlyAdvancedChangesAreUnsaved =
    dirty && !graphSelectionDirty && !simpleDirty && !reviewDirty;
  const leavePromptMessage = graphSelectionDirty
    ? "Use or clear the graph box first."
    : simpleDirty
      ? "Check the edited values first."
      : reviewDirty
        ? "Save the pump setup first."
        : "Save these changes before leaving.";
  const visibleRecentImports =
    recentImports.data?.items.filter(
      (item) =>
        item.state !== "rejected_insufficient" &&
        !hiddenImportIds.has(item.id),
    ) ?? [];
  const archivedRecentImports =
    recentImports.data?.items.filter(
      (item) =>
        item.state === "rejected_insufficient" &&
        !hiddenImportIds.has(item.id),
    ) ?? [];

  return (
    <Page
      actions={
        activeImport === null ? (
          <button
            disabled={showSourceUpload || jobId !== null || busyAction !== null}
            type="button"
            onClick={() => {
              clearSourceSelection();
              setError("");
              setNotice("");
              setShowSourceUpload(true);
            }}
          >
            Add pump curve
          </button>
        ) : (
          <button
            disabled={
              stateReplacingBusy || jobId !== null || busyAction !== null
            }
            type="button"
            onClick={(event) =>
              requestReturnToCurveLibrary(event.currentTarget)
            }
          >
            Back to curves
          </button>
        )
      }
      title="Pump curves"
    >
      {error === "" ? null : (
        <p className="curve-alert" role="alert">
          {error}
        </p>
      )}
      {notice === "" ? null : (
        <p className="curve-notice" role="status">
          {notice}
        </p>
      )}

      {showLeavePrompt ? (
        <div
          ref={leaveDialogRef}
          aria-labelledby="curve-leave-title"
          aria-modal="true"
          className="curve-confirmation"
          role="dialog"
        >
          <div className="curve-confirmation__surface">
            <h3 id="curve-leave-title">Unsaved changes</h3>
            <p>{leavePromptMessage}</p>
            {leaveError === "" ? null : (
              <p className="curve-alert" role="alert">
                {leaveError}
              </p>
            )}
            <div className="curve-confirmation__actions">
              <button
                ref={leaveStayButtonRef}
                disabled={busyAction !== null}
                type="button"
                onClick={closeLeavePrompt}
              >
                Stay here
              </button>
              <button
                disabled={busyAction !== null}
                type="button"
                onClick={() => returnToCurveLibrary(true)}
              >
                Leave without saving
              </button>
              {onlyAdvancedChangesAreUnsaved ? (
                <button
                  className="curve-primary-action"
                  disabled={busyAction !== null}
                  type="button"
                  onClick={() => void saveAdvancedChangesAndExit()}
                >
                  {busyAction === "save-exit" ? "Saving…" : "Save and exit"}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {activeImport === null ? (
        <>
          {!showSourceUpload ? <Card className="curve-library">
            {recentImports.isLoading ? <p>Loading pump curves…</p> : null}
            <ul className="curve-recent-list">
              {visibleRecentImports.map((item) => (
                <li key={item.id}>
                  <div>
                    <strong>{item.source_filename}</strong>
                    <span>{stateLabel(item.state, item.source_format)}</span>
                    <small>{new Date(item.updated_at).toLocaleString()}</small>
                  </div>
                  <div className="curve-recent-actions">
                    <button
                      disabled={busyAction !== null}
                      type="button"
                      onClick={() => void openImport(item.id)}
                    >
                      Continue
                    </button>
                    {item.state === "verified" ? null : (
                      <button
                        aria-label={`Delete ${item.source_filename}`}
                        className="curve-danger"
                        disabled={busyAction !== null}
                        type="button"
                        onClick={() => setDashboardDelete(item)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            {visibleRecentImports.length === 0 && !recentImports.isLoading ? (
              <div className="curve-empty-state">
                <h3>
                  {archivedRecentImports.length === 0
                    ? "No pump curves yet"
                    : "No active curves"}
                </h3>
              </div>
            ) : null}
            {archivedRecentImports.length === 0 ? null : (
              <div className="curve-archive-list">
                <button
                  aria-expanded={showArchivedImports}
                  type="button"
                  onClick={() => setShowArchivedImports((shown) => !shown)}
                >
                  Archived ({archivedRecentImports.length})
                </button>
                {showArchivedImports ? (
                  <ul
                    aria-label="Archived pump curves"
                    className="curve-recent-list"
                  >
                    {archivedRecentImports.map((item) => (
                      <li key={item.id}>
                        <div>
                          <strong>{item.source_filename}</strong>
                          <span>
                            {stateLabel(item.state, item.source_format)}
                          </span>
                          <small>
                            {new Date(item.updated_at).toLocaleString()}
                          </small>
                        </div>
                        <button
                          disabled={busyAction !== null}
                          type="button"
                          onClick={() => void openImport(item.id)}
                        >
                          View
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            )}
          </Card> : null}

          {dashboardDelete === null ? null : (
            <div
              aria-labelledby="curve-dashboard-delete-title"
              aria-modal="true"
              className="curve-confirmation"
              role="dialog"
            >
              <div className="curve-confirmation__surface">
                <h3 id="curve-dashboard-delete-title">
                  Delete {dashboardDelete.source_filename}?
                </h3>
                <p>This removes it from this list. Required history is kept.</p>
                <div className="curve-confirmation__actions">
                  <button
                    disabled={busyAction !== null}
                    type="button"
                    onClick={() => setDashboardDelete(null)}
                  >
                    Cancel
                  </button>
                  <button
                    aria-label={`Confirm delete ${dashboardDelete.source_filename}`}
                    className="curve-danger curve-danger--solid"
                    disabled={busyAction !== null}
                    type="button"
                    onClick={() => void deleteDashboardImport()}
                  >
                    {busyAction === "delete-dashboard" ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showSourceUpload ? (
            <Card className="curve-simple-card" title="Add pump curve">
              <div className="curve-upload">
                <label>
                  Pump curve source
                  <input
                    ref={uploadInputRef}
                    {...guided(curveImportFieldHelp.sourceFile)}
                    accept=".pdf,.png,.jpg,.jpeg,.csv,application/pdf,image/png,image/jpeg,text/csv"
                    aria-label="Pump curve source"
                    disabled={capabilities.data === undefined || busyAction !== null}
                    onClick={() =>
                      reportLifecycleDiagnostic(
                        "file_selection_opened",
                        "curve_import",
                        "started",
                      )
                    }
                    type="file"
                    onChange={chooseFile}
                  />
                </label>
                <div className="curve-button-row">
                  <button
                    className="curve-primary-action"
                    disabled={selectedFile === null || capabilities.data === undefined || busyAction !== null}
                    type="button"
                    onClick={() => { if (selectedFile !== null) void upload(selectedFile); }}
                  >
                    {busyAction !== null ? "Adding…" : "Add vendor sheet"}
                  </button>
                  <button
                    disabled={busyAction !== null}
                    type="button"
                    onClick={() => {
                      clearSourceSelection();
                      setError("");
                      setShowSourceUpload(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
                <details className="curve-technical-disclosure">
                  <summary>File limits and CSV option</summary>
                  <p>
                    PDF, PNG, JPEG, or the{" "}
                    <a href="/api/v1/curve-import/manual.csv">
                      manual CSV template
                    </a>
                    .
                    {capabilities.data === undefined
                      ? " Loading file limits…"
                      : " Maximum " +
                        capabilities.data.max_file_bytes.toLocaleString() +
                        " bytes and " +
                        capabilities.data.max_pages +
                        " pages."}
                  </p>
                </details>
              </div>
            </Card>
          ) : null}
        </>
      ) : (
        <>
          <ol className="curve-simple-steps" aria-label="Pump curve progress">
            <li
              aria-current={!graphSelected ? "step" : undefined}
              aria-label={
                graphStepApplies
                  ? "Step 1: Select graph, " +
                    (graphSelected ? "completed" : "current")
                  : "Step 1: Vendor sheet, completed"
              }
              className={
                graphSelected
                  ? graphStepApplies
                    ? "is-complete is-reopenable"
                    : "is-complete"
                  : "is-current"
              }
            >
              {graphSelected && graphStepApplies ? (
                <button
                  aria-label="Edit selected graph"
                  type="button"
                  onClick={() =>
                    setGraphEditorRequest((request) => request + 1)
                  }
                >
                  <span>1</span>
                  Select graph
                  <small>Edit</small>
                </button>
              ) : (
                <>
                  <span>1</span>
                  {graphStepApplies ? "Select graph" : "Vendor sheet"}
                </>
              )}
            </li>
            <li
              aria-current={
                curveDataReady ||
                activeImport.state === "rejected_insufficient"
                  ? undefined
                  : graphSelected
                    ? "step"
                    : undefined
              }
              aria-label={
                curveDataReady
                  ? "Step 2: Review curve data, " +
                    (graphSelected ? "completed" : "pending")
                  : activeImport.state === "rejected_insufficient"
                    ? "Step 2: Review curve data, stopped"
                    : "Step 2: Review curve data, " +
                      (graphSelected ? "current" : "pending")
              }
              className={
                curveDataReady
                  ? graphSelected
                    ? activeImport.state === "needs_review"
                      ? "is-complete is-reopenable"
                      : "is-complete"
                    : ""
                  : activeImport.state === "rejected_insufficient"
                    ? "is-stopped"
                    : graphSelected
                      ? "is-current"
                      : ""
              }
            >
              {activeImport.state === "needs_review" &&
              hasReviewableTraces &&
              graphSelected ? (
                <button
                  aria-label="Edit curve data"
                  type="button"
                  onClick={openAdvancedTools}
                >
                  <span>2</span>
                  Review curve data
                  <small>Edit</small>
                </button>
              ) : (
                <>
                  <span>2</span>Review curve data
                </>
              )}
            </li>
            <li
              aria-current={
                activeImport.state === "needs_review" &&
                hasReviewableTraces &&
                graphSelected
                  ? "step"
                  : undefined
              }
              aria-label={
                activeImport.state === "verified"
                  ? "Step 3: Save to pump, completed"
                  : activeImport.state === "needs_review" &&
                      hasReviewableTraces
                    ? "Step 3: Save to pump, " +
                      (graphSelected ? "current" : "pending")
                    : activeImport.state === "rejected_insufficient"
                      ? "Step 3: Save to pump, unavailable"
                      : "Step 3: Save to pump, pending"
              }
              className={
                activeImport.state === "verified"
                  ? "is-complete"
                  : activeImport.state === "needs_review" &&
                      hasReviewableTraces &&
                      graphSelected
                    ? "is-current"
                    : ""
              }
            >
              <span>3</span>Save to pump
            </li>
          </ol>

          <Card className="curve-source-summary">
            <div className="curve-simple-heading">
              <div>
                <p className="eyebrow">Vendor sheet</p>
                <h2>{activeImport.source.original_filename}</h2>
              </div>
              <span className={"curve-state is-" + activeImport.state}>
                {stateLabel(activeImport.state, activeImport.source_format)}
              </span>
            </div>
            {activeImport.state === "uploaded" || jobId !== null ? (
              <div className="curve-processing-simple">
                <div className="curve-button-row">
                  <button
                    disabled={jobId !== null || busyAction !== null}
                    type="button"
                    onClick={() => void startProcessing()}
                  >
                    {busyAction === "parse" ? "Starting…" : "Read sheet"}
                  </button>
                  {jobId === null ? null : (
                    <button
                      disabled={busyAction !== null}
                      type="button"
                      onClick={() => void cancelProcessing()}
                    >
                      Stop
                    </button>
                  )}
                </div>
              </div>
            ) : null}
            {displayedJob === null || jobId === null ? null : (
              <div className="curve-job" aria-live="polite">
                <div>
                  <strong>{displayedJob.state.replace("_", " ")}</strong>
                  <span>{displayedJob.message}</span>
                </div>
                <progress
                  aria-label="Vendor sheet processing progress"
                  max="1"
                  value={displayedJob.progress_fraction}
                >
                  {Math.round(displayedJob.progress_fraction * 100)}%
                </progress>
              </div>
            )}
          </Card>

          {activeImport.source_format === "pdf" &&
          activeImport.state !== "uploaded" &&
          activeImport.state !== "rejected_insufficient" &&
          activeImport.state !== "verified" ? (
            <Card className="curve-analysis-card">
              <CurveAnalysisPanel
                key={activeImport.id}
                onBeforeAnalysis={async () => {
                  const resolved = await resolveUploadedCurve(activeImport);
                  if (resolved === null) return true;
                  activate(resolved);
                  setNotice("Curve data is ready to review.");
                  void queryClient.invalidateQueries({ queryKey: importKeys.all });
                  return false;
                }}
                api={api}
                capabilities={capabilities.data ?? null}
                document={history.present}
                graphEditorRequest={graphEditorRequest}
                hasUnsavedChanges={hasAnyUnsavedChanges}
                graphSelectionDisabled={
                  hasNonGraphUnsavedChanges || stateReplacingBusy
                }
                imported={activeImport}
                onBusyChange={setAiApplyBusy}
                onError={setError}
                onImported={acceptImported}
                onGraphSelectionDirty={setGraphSelectionDirty}
                onNotice={setNotice}
              />
            </Card>
          ) : null}

          {simpleEditorVisible ? (
            <Card className="curve-simple-card">
              {activeImport.state === "needs_review" ||
              activeImport.source_format === "pdf" ? (
                <details className="curve-edit-values">
                  <summary>
                    {activeImport.state === "needs_review" &&
                    hasReviewableTraces
                      ? "Edit the checked pump values"
                      : "Enter values manually"}
                  </summary>
                  <SimpleCurveDataEditor
                    busy={busyAction === "check"}
                    disabled={
                      busyAction !== null ||
                      stateReplacingBusy ||
                      graphSelectionDirty
                    }
                    document={history.present}
                    onDirtyChange={setSimpleDirty}
                    suggestedRpms={activeImport.suggestions.speeds_rpm}
                    onCheck={checkSimpleDocument}
                  />
                </details>
              ) : (
                <SimpleCurveDataEditor
                  busy={busyAction === "check"}
                  disabled={
                    busyAction !== null ||
                    stateReplacingBusy ||
                    graphSelectionDirty
                  }
                  document={history.present}
                  onDirtyChange={setSimpleDirty}
                  suggestedRpms={activeImport.suggestions.speeds_rpm}
                  onCheck={checkSimpleDocument}
                />
              )}
            </Card>
          ) : activeImport.state === "rejected_insufficient" ? (
            <Card title="This source could not be used">
              <p>
                {activeImport.findings[0]?.message ??
                  "The source did not contain enough safe pump-curve information."}
              </p>
              <p>
                Open Advanced curve tools to inspect the retained source and
                audit details.
              </p>
            </Card>
          ) : null}

          {(activeImport.state === "needs_review" && hasReviewableTraces) ||
          activeImport.state === "verified" ? (
            <Card className="curve-review-card">
              <ReviewPublicationPanel
                key={activeImport.id + ":" + activeImport.updated_at}
                api={api}
                catalog={catalog}
                library={library}
                disabled={stateReplacingBusy || graphSelectionDirty}
                onBusyChange={setReviewBusy}
                document={history.present}
                hasUnsavedChanges={dirty || simpleDirty || graphSelectionDirty}
                imported={activeImport}
                onDirtyChange={setReviewDirty}
                onError={setError}
                onImported={acceptImported}
                onNotice={setNotice}
                onCorrect={openAdvancedTools}
              />
            </Card>
          ) : null}

          <Card className="curve-advanced-shell">
            <details ref={advancedToolsRef}>
              <summary>Advanced curve tools</summary>
              <p className="curve-help">
                Trace graphs, correct axes, add curve types, or review source
                details.
              </p>

              <section
                className="curve-provenance"
                aria-label="Source and technical details"
              >
                <h3>Source and technical details</h3>
                <div className="curve-status-row">
                  <span>{activeImport.source_format.toUpperCase()}</span>
                  <span>{activeImport.page_count ?? "—"} pages</span>
                  <span>
                    {activeImport.source.byte_size.toLocaleString()} bytes
                  </span>
                </div>
                <dl>
                  <div>
                    <dt>SHA-256</dt>
                    <dd>
                      <code>{activeImport.source.sha256}</code>
                    </dd>
                  </div>
                  <div>
                    <dt>Parser</dt>
                    <dd>{activeImport.parser_version}</dd>
                  </div>
                  <div>
                    <dt>Embedded text</dt>
                    <dd>
                      {activeImport.suggestions.used_embedded_text
                        ? "Used"
                        : "Not used"}
                    </dd>
                  </div>
                  <div>
                    <dt>OCR</dt>
                    <dd>
                      {activeImport.suggestions.used_ocr ? "Used" : "Not used"}
                    </dd>
                  </div>
                </dl>

                {activeImport.state === "uploaded" ? (
                  <div className="curve-processing-controls">
                    <label className="curve-inline-check">
                      <input
                        {...guided(curveImportFieldHelp.localOcr)}
                        checked={enableOcr}
                        disabled={capabilities.data?.ocr_available !== true}
                        type="checkbox"
                        onChange={(event) => setEnableOcr(event.target.checked)}
                      />
                      Retry with local OCR
                    </label>
                    <label className="curve-inline-check">
                      <input
                        {...guided(curveImportFieldHelp.selectedPageOnly)}
                        checked={selectedPageOnly}
                        type="checkbox"
                        onChange={(event) =>
                          setSelectedPageOnly(event.target.checked)
                        }
                      />
                      Read only the selected page
                    </label>
                    <button
                      disabled={jobId !== null || busyAction !== null}
                      type="button"
                      onClick={() => void startProcessing()}
                    >
                      Read sheet again
                    </button>
                  </div>
                ) : null}

                <section aria-labelledby="curve-parser-suggestions-title">
                  <h3 id="curve-parser-suggestions-title">
                    Values found on the sheet
                  </h3>
                  <p className="curve-help">
                    These are hints only. Compare every value with the
                    manufacturer sheet before saving.
                  </p>
                  <dl>
                    <div>
                      <dt>Manufacturer</dt>
                      <dd>
                        {activeImport.suggestions.manufacturer ?? "Not found"}
                      </dd>
                    </div>
                    <div>
                      <dt>Model</dt>
                      <dd>{activeImport.suggestions.model ?? "Not found"}</dd>
                    </div>
                    <div>
                      <dt>Curve number / revision</dt>
                      <dd>
                        {[
                          activeImport.suggestions.curve_number,
                          activeImport.suggestions.curve_revision,
                        ]
                          .filter(Boolean)
                          .join(" / ") || "Not found"}
                      </dd>
                    </div>
                    <div>
                      <dt>Speeds</dt>
                      <dd>
                        {activeImport.suggestions.speeds_rpm.length === 0
                          ? "Not found"
                          : activeImport.suggestions.speeds_rpm.join(", ") +
                            " RPM"}
                      </dd>
                    </div>
                  </dl>
                </section>

                <DiscardImportPanel
                  api={api}
                  disabled={stateReplacingBusy || graphSelectionDirty}
                  imported={activeImport}
                  onDiscarded={acceptDiscarded}
                  onBusyChange={setDiscardBusy}
                  onError={setError}
                  onImported={acceptImported}
                  onNotice={setNotice}
                />
              </section>

              {editable &&
              (graphSelectionDirty || simpleDirty || stateReplacingBusy) ? (
                <p className="curve-verification-gate" role="status">
                  {stateReplacingBusy
                    ? "Saving the curve. Editing will resume when it finishes."
                    : graphSelectionDirty
                      ? "Use or clear the graph box before advanced editing."
                      : "Check the simple pump values above before using advanced curve editing."}
                </p>
              ) : editable ? (
                <section className="curve-editor-card">
                  <div className="curve-editor-intro">
                    <p className="eyebrow">Curve editor</p>
                    <h3 ref={advancedEditorHeadingRef} tabIndex={-1}>
                      Correct traced curves
                    </h3>
                    <p>
                      Select a curve, make the correction, then check changes.
                    </p>
                  </div>
                  <div className="curve-editor-toolbar">
                    <div>
                      <button
                        disabled={history.past.length === 0}
                        type="button"
                        onClick={() => {
                          dispatch({ type: "undo" });
                          setDirty(true);
                        }}
                      >
                        Undo
                      </button>
                      <button
                        disabled={history.future.length === 0}
                        type="button"
                        onClick={() => {
                          dispatch({ type: "redo" });
                          setDirty(true);
                        }}
                      >
                        Redo
                      </button>
                    </div>
                    <span>
                      {dirty
                        ? "Unsaved advanced changes"
                        : "Advanced draft is saved"}
                    </span>
                    <button
                      disabled={!dirty || busyAction !== null}
                      type="button"
                      onClick={() => void saveDraft()}
                    >
                      {busyAction === "save"
                        ? "Checking…"
                        : "Check advanced changes"}
                    </button>
                  </div>
                  <CurveDigitizer
                    activeTraceId={activeTraceId}
                    api={api}
                    document={history.present}
                    importId={activeImport.id}
                    pageCount={activeImport.page_count}
                    sourceFormat={activeImport.source_format}
                    suggestedSpeeds={activeImport.suggestions.speeds_rpm}
                    onActiveTraceChange={setActiveTraceId}
                    onBusyChange={setAdvancedEditorBusy}
                    onCommit={commitDocument}
                    onError={setError}
                  />
                  <CalibrationEditor
                    activeTrace={activeTrace}
                    document={history.present}
                    onCommit={commitDocument}
                  />
                </section>
              ) : null}
            </details>
          </Card>
          <div className="curve-button-row">
            <button
              aria-label="Back to curves from end of workbench"
              disabled={
                stateReplacingBusy || jobId !== null || busyAction !== null
              }
              type="button"
              onClick={(event) =>
                requestReturnToCurveLibrary(event.currentTarget)
              }
            >
              Back to curves
            </button>
          </div>
        </>
      )}
    </Page>
  );
}
